import { apiGet, apiPost, apiPatch, apiDelete } from "../lib/api-client";

// API client for the WS-9 admin content surface (routes/admin-content.ts,
// mounted at /admin/content). Kept separate from api/admin.ts, mirroring the
// API-side file split.

// ─── Collections ────────────────────────────────────────────────────────────

export type AdminCollectionType = "peaks" | "trails" | "landmarks" | "regions";
export type AdminCollectionItemType = "peak" | "trail" | "landmark" | "region";

export interface AdminCollection {
  id: string;
  slug: string;
  name: string;
  type: AdminCollectionType;
  description: string | null;
  region: string | null;
  country: string | null;
  coverImage: string | null;
  sortOrder: number;
  itemCount: number;
  createdAt: string;
}

export function fetchAdminCollections(): Promise<{ collections: AdminCollection[] }> {
  return apiGet("/admin/content/collections");
}

export interface AdminCollectionCreate {
  slug?: string;
  name: string;
  type: AdminCollectionType;
  description?: string | null;
  region?: string | null;
  country?: string | null;
  coverImage?: string | null;
  sortOrder?: number;
}

export function createAdminCollection(data: AdminCollectionCreate): Promise<{ collection: AdminCollection }> {
  return apiPost("/admin/content/collections", data);
}

export type AdminCollectionPatch = Partial<
  Pick<AdminCollectionCreate, "name" | "description" | "region" | "country" | "coverImage" | "sortOrder">
>;

export function patchAdminCollection(
  id: string,
  data: AdminCollectionPatch,
): Promise<{ collection: AdminCollection }> {
  return apiPatch(`/admin/content/collections/${id}`, data);
}

export function deleteAdminCollection(id: string, force = false): Promise<{ ok: boolean; itemsDeleted: number }> {
  return apiDelete(`/admin/content/collections/${id}${force ? "?force=true" : ""}`);
}

export interface AdminCollectionItem {
  id: string;
  itemType: AdminCollectionItemType;
  itemId: string;
  sortOrder: number;
  name: string;
  slug: string | null;
  meta: string | null;
}

export function fetchAdminCollectionItems(collectionId: string): Promise<{ items: AdminCollectionItem[] }> {
  return apiGet(`/admin/content/collections/${collectionId}/items`);
}

export function addAdminCollectionItem(
  collectionId: string,
  data: { itemType: AdminCollectionItemType; itemId: string },
): Promise<{ item: AdminCollectionItem }> {
  return apiPost(`/admin/content/collections/${collectionId}/items`, data);
}

export function removeAdminCollectionItem(collectionId: string, itemId: string): Promise<{ ok: boolean }> {
  return apiDelete(`/admin/content/collections/${collectionId}/items/${itemId}`);
}

export interface AdminSearchItem {
  id: string;
  name: string;
  slug: string;
  meta: string | null;
}

export function searchAdminItems(
  type: AdminCollectionItemType,
  q: string,
): Promise<{ items: AdminSearchItem[] }> {
  return apiGet(`/admin/content/search-items?type=${type}&q=${encodeURIComponent(q)}`);
}

// ─── Achievement definitions ────────────────────────────────────────────────

export type AdminAchievementTier = "bronze" | "silver" | "gold" | "platinum" | "mythic";

export type AchievementRuleType =
  | "peak_count"
  | "peak_elevation"
  | "multi_peak_day"
  | "activity_distance"
  | "early_start"
  | "collection_complete"
  | "trail_complete"
  | "landmark_count"
  | "distinct_countries"
  | "region_coverage"
  | "total_distance"
  | "total_elevation_gain"
  | "total_activities"
  | "total_days_out"
  | "peak_summited"
  | "peak_count_country"
  | "landmark_category_count"
  | "collection_pct"
  | "collections_completed_count"
  | "trails_completed_count"
  | "trails_completed_in_set"
  | "collections_complete_all"
  | "trail_every_route"
  | "trail_sections_count"
  | "regions_fully_explored_count"
  | "activity_elevation_gain"
  | "activity_duration"
  | "finish_after_hour"
  | "spans_midnight"
  | "activity_on_date"
  | "months_in_year"
  | "distinct_seasons"
  | "same_day_count"
  | "weekly_streak"
  | "summit_month"
  | "collection_in_one_activity"
  | "trail_complete_in_one_activity";

export interface AdminAchievement {
  id: string;
  slug: string;
  name: string;
  description: string;
  tier: AdminAchievementTier;
  points: number;
  iconName: string | null;
  category: string | null;
  ruleType: AchievementRuleType;
  ruleParams: Record<string, unknown>;
  rarity: number | null;
  createdAt: string;
  unlockedCount: number;
}

export function fetchAdminAchievements(): Promise<{ achievements: AdminAchievement[] }> {
  return apiGet("/admin/content/achievements");
}

export interface AdminAchievementCreate {
  slug?: string;
  name: string;
  description: string;
  tier: AdminAchievementTier;
  points: number;
  iconName?: string | null;
  category?: string | null;
  ruleType: AchievementRuleType;
  ruleParams: Record<string, unknown>;
}

export function createAdminAchievement(
  data: AdminAchievementCreate,
): Promise<{ achievement: AdminAchievement }> {
  return apiPost("/admin/content/achievements", data);
}

export type AdminAchievementPatch = Partial<Omit<AdminAchievementCreate, "slug">>;

export function patchAdminAchievement(
  id: string,
  data: AdminAchievementPatch,
): Promise<{ achievement: AdminAchievement }> {
  return apiPatch(`/admin/content/achievements/${id}`, data);
}

export function deleteAdminAchievement(id: string): Promise<{ ok: boolean }> {
  return apiDelete(`/admin/content/achievements/${id}`);
}

// ─── Events (WS-11) ─────────────────────────────────────────────────────────

export type AdminEventRuleType = "peak_count" | "trail_section_count" | "landmark_count" | "total_distance";

export interface AdminEvent {
  id: string;
  slug: string;
  name: string;
  description: string;
  startsAt: string;
  endsAt: string;
  ruleType: AdminEventRuleType;
  ruleParams: Record<string, unknown>;
  rewardItemIds: string[];
  rewardWaypoints: number;
  active: boolean;
  createdAt: string;
  completions: number;
}

export function fetchAdminEvents(): Promise<{ events: AdminEvent[]; ruleTypes: AdminEventRuleType[] }> {
  return apiGet("/admin/content/events");
}

export interface AdminEventCreate {
  slug?: string;
  name: string;
  description: string;
  startsAt: string;
  endsAt: string;
  ruleType: AdminEventRuleType;
  ruleParams: Record<string, unknown>;
  rewardItemIds: string[];
  rewardWaypoints: number;
  active?: boolean;
}

export function createAdminEvent(data: AdminEventCreate): Promise<{ event: AdminEvent }> {
  return apiPost("/admin/content/events", data);
}

export type AdminEventPatch = Partial<Omit<AdminEventCreate, "slug">>;

export function patchAdminEvent(id: string, data: AdminEventPatch): Promise<{ event: AdminEvent }> {
  return apiPatch(`/admin/content/events/${id}`, data);
}

export function deleteAdminEvent(id: string, force = false): Promise<{ ok: boolean }> {
  return apiDelete(`/admin/content/events/${id}${force ? "?force=true" : ""}`);
}
